/**
 * AdminUsers — User management list with search, filters, pagination, CSV export
 * Glassmorphism dark theme with SparkLine SVG charts
 */
import { useState, useEffect, useCallback, useRef } from 'preact/hooks';
import { api } from '../api/client';
import { isLoggedIn } from '../stores/auth.store';
import { LoginPage } from './Login';
import { route } from 'preact-router';
import { Button, Input, Select } from '../components/ui';
import { T } from '../styles/tokens';

// ── Types ──
interface UserRow {
  id: string;
  email: string;
  name: string | null;
  role: string;
  status: string;
  created_at: string;
  is_premium: number;
  premium_plan: string | null;
  subscription_plan_active: string | null;
  is_suspended: number;
  token_limit_monthly: number;
  tokens_used_monthly: number;
  budget_limit_monthly: number;
  budget_used_monthly: number;
  budget_limit_daily: number;
  budget_used_daily: number;
  rpm_limit: number;
  max_machines: number;
  login_count: number;
  last_login_at: string | null;
  last_activity_at: string | null;
  referral_code: string | null;
  avatar_url: string | null;
  wallet_balance: number | null;
}

interface Stats {
  total: number;
  active: number;
  paying: number;
  suspended: number;
  tokens_today: number;
  budget_today: number;
}

// ── Helpers ──
function timeAgo(dateStr: string | null): string {
  if (!dateStr) return 'Nunca';
  const diff = Date.now() - new Date(dateStr).getTime();
  if (diff < 60_000) return `${Math.round(diff / 1000)}s atras`;
  if (diff < 3600_000) return `${Math.round(diff / 60000)}m atras`;
  if (diff < 86400_000) return `${Math.round(diff / 3600000)}h atras`;
  return `${Math.floor(diff / 86400000)}d atras`;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('pt-BR');
}

function formatNum(n: number | null | undefined): string {
  if (n == null) return '0';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

const PLAN_COLORS: Record<string, string> = { free: T.textMuted, pro: T.primary, business: T.purple, enterprise: T.warning };

// ── Components ──
function Avatar({ name, url, size = 32 }: { name: string | null; url: string | null; size?: number }) {
  if (url) return <img src={url} alt="" style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover' }} />;
  const letter = (name || '?').charAt(0).toUpperCase();
  const hash = (name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  const hue = hash % 360;
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: `linear-gradient(135deg, hsl(${hue}, 50%, 40%), hsl(${(hue + 40) % 360}, 50%, 30%))`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.45, fontWeight: 700, color: T.text, flexShrink: 0,
    }}>{letter}</div>
  );
}

function Badge({ text, color }: { text: string; color: string }) {
  return (
    <span style={{
      padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 600,
      background: `${color}18`, color, border: `1px solid ${color}30`,
    }}>{text}</span>
  );
}

function SparkLine({ data }: { data: number[] }) {
  if (!data || data.length === 0) return <span style={{ color: T.textMuted, fontSize: 10 }}>—</span>;
  const max = Math.max(...data, 1);
  const w = 56, h = 20, barW = 6, gap = 2;
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
      {data.slice(-7).map((v, i) => {
        const barH = Math.max(1, (v / max) * (h - 2));
        return <rect key={i} x={i * (barW + gap)} y={h - barH} width={barW} height={barH} rx={1} fill="#6C8EF2" opacity={0.8} />;
      })}
    </svg>
  );
}

function StatCard({ label, value, icon, color = T.primary }: { label: string; value: string | number; icon: string; color?: string }) {
  return (
    <div style={{
      background: T.bgCard, backdropFilter: 'blur(12px)',
      border: `1px solid ${T.whiteAlpha8}`, borderRadius: 12,
      padding: '14px 16px', minWidth: 120, flex: 1,
    }}>
      <div style={{ fontSize: 11, color: T.textSecondary, marginBottom: 4 }}>{icon} {label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color }}>{value}</div>
    </div>
  );
}

function Skeleton({ width = '100%', height = 16 }: { width?: string | number; height?: number }) {
  return <div style={{ width, height, borderRadius: 6, background: T.border, animation: 'pulse 1.5s infinite' }} />;
}

// ── Styles ──
const styles = {
  page: {
    minHeight: '100vh', background: T.bg, color: T.text, padding: '24px 32px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
  } as const,
  glass: {
    background: T.bgCard, backdropFilter: 'blur(16px)',
    border: `1px solid ${T.border}`, borderRadius: 16,
  } as const,
  input: {
    background: T.border, border: `1px solid ${T.borderHover}`,
    borderRadius: 8, padding: '8px 12px', color: T.text, fontSize: 13, outline: 'none',
  } as const,
  select: {
    background: T.border, border: `1px solid ${T.borderHover}`,
    borderRadius: 8, padding: '8px 12px', color: T.text, fontSize: 12, outline: 'none',
  } as const,
  btn: {
    padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
    fontSize: 12, fontWeight: 600, transition: 'all 0.15s',
  } as const,
  th: {
    padding: '10px 12px', textAlign: 'left' as const, fontSize: 11, fontWeight: 600,
    color: T.textSecondary, borderBottom: `1px solid ${T.border}`,
    whiteSpace: 'nowrap' as const,
  },
  td: {
    padding: '10px 12px', fontSize: 13, borderBottom: `1px solid ${T.bgCard}`,
    whiteSpace: 'nowrap' as const,
  },
};

export function AdminUsers() {
  if (!isLoggedIn.value) return <LoginPage />;

  const [users, setUsers] = useState<UserRow[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPlan, setFilterPlan] = useState('');
  const [filterSuspended, setFilterSuspended] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [toast, setToast] = useState<string | null>(null);
  const limit = 50;
  const intervalRef = useRef<any>(null);

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(null), 3000); };

  const fetchUsers = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (search) params.set('search', search);
      if (filterRole) params.set('role', filterRole);
      if (filterStatus) params.set('status', filterStatus);
      if (filterPlan) params.set('plan', filterPlan);
      if (filterSuspended) params.set('suspended', filterSuspended);

      const res = await api.get<{ users: UserRow[]; pagination: { total: number } }>(`/admin/users?${params}`);
      setUsers(res.users);
      setTotal(res.pagination.total);
    } catch (e: any) {
      console.error('Failed to fetch users:', e);
    } finally {
      setLoading(false);
    }
  }, [page, search, filterRole, filterStatus, filterPlan, filterSuspended]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get<Stats>('/admin/users/stats');
      setStats(res);
    } catch (e) { console.error('Failed to fetch stats:', e); }
  }, []);

  useEffect(() => { fetchUsers(); fetchStats(); }, [fetchUsers, fetchStats]);

  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(() => { fetchUsers(); fetchStats(); }, 30000);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [autoRefresh, fetchUsers, fetchStats]);

  const exportCSV = async () => {
    try {
      const res = await fetch('/api/v1/admin/users/export', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `users-${new Date().toISOString().split('T')[0]}.csv`;
      a.click(); URL.revokeObjectURL(url);
      showToast('CSV exportado!');
    } catch (e) { showToast('Falha na exportacao'); }
  };

  const handleSuspend = async (userId: string, isSuspended: boolean) => {
    try {
      if (isSuspended) {
        await api.post(`/admin/users/${userId}/unsuspend`, {});
        showToast('Suspensao removida');
      } else {
        await api.post(`/admin/users/${userId}/suspend`, { reason: 'Acao administrativa' });
        showToast('Usuario suspenso');
      }
      fetchUsers();
    } catch (e) { showToast('Falha na acao'); }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div style={styles.page}>
      <style>{`@keyframes pulse { 0%, 100% { opacity: 0.6; } 50% { opacity: 0.3; } }`}</style>

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 9999,
          background: T.bgSecondary, color: T.text, padding: '10px 20px',
          borderRadius: 10, fontSize: 13, border: '1px solid rgba(108,142,242,0.3)',
          boxShadow: `0 8px 24px ${T.blackAlpha40}`,
        }}>{toast}</div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>Gerenciamento de Usuarios</h1>
          <p style={{ fontSize: 13, color: T.textSecondary, margin: '4px 0 0' }}>Gerenciar todos os usuarios da plataforma</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant="secondary" size="sm" onClick={exportCSV}>Exportar CSV</Button>
          <Button variant={autoRefresh ? 'secondary' : 'ghost'} size="sm" onClick={() => setAutoRefresh(!autoRefresh)}>
            {autoRefresh ? 'Auto' : 'Pausado'}
          </Button>
        </div>
      </div>

      {/* Stats Bar */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        {stats ? (
          <>
            <StatCard label="Total Usuarios" value={stats.total} icon="" />
            <StatCard label="Ativos" value={stats.active} icon="" color={T.success} />
            <StatCard label="Pagantes" value={stats.paying} icon="" color={T.warning} />
            <StatCard label="Suspensos" value={stats.suspended} icon="" color={T.danger} />
            <StatCard label="Tokens Hoje" value={formatNum(stats.tokens_today)} icon="" />
            <StatCard label="Orcamento Hoje" value={`$${(stats.budget_today / 100).toFixed(2)}`} icon="" color={T.success} />
          </>
        ) : Array.from({ length: 6 }).map((_, i) => (
          <div key={i} style={{ ...styles.glass, padding: '14px 16px', flex: 1, minWidth: 120 }}>
            <Skeleton width={60} height={12} /><div style={{ height: 6 }} /><Skeleton width={40} height={22} />
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ ...styles.glass, padding: 16, marginBottom: 16, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <Input
          placeholder="Buscar nome, email..." value={search}
          onChange={(v) => { setSearch(v); setPage(1); }}
          style={{ ...styles.input, flex: 1, minWidth: 200 }}
        />
        <Select value={filterRole} onChange={(v) => { setFilterRole(v); setPage(1); }} style={styles.select}
          options={[{ value: '', label: 'Todas Funcoes' }, { value: 'ADMIN', label: 'Admin' }, { value: 'USER', label: 'Usuario' }]} />
        <Select value={filterStatus} onChange={(v) => { setFilterStatus(v); setPage(1); }} style={styles.select}
          options={[{ value: '', label: 'Todos Status' }, { value: 'active', label: 'Ativo' }, { value: 'inactive', label: 'Inativo' }]} />
        <Select value={filterPlan} onChange={(v) => { setFilterPlan(v); setPage(1); }} style={styles.select}
          options={[{ value: '', label: 'Todos Planos' }, { value: 'free', label: 'Free' }, { value: 'pro', label: 'Pro' }, { value: 'business', label: 'Business' }, { value: 'enterprise', label: 'Enterprise' }]} />
        <Select value={filterSuspended} onChange={(v) => { setFilterSuspended(v); setPage(1); }} style={styles.select}
          options={[{ value: '', label: 'Suspenso?' }, { value: '1', label: 'Suspenso' }, { value: '0', label: 'Nao Suspenso' }]} />
      </div>

      {/* Table */}
      <div style={{ ...styles.glass, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={styles.th}>Usuario</th>
              <th style={styles.th}>Funcao</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Plano</th>
              <th style={styles.th}>Online</th>
              <th style={styles.th}>Uso LLM (7d)</th>
              <th style={styles.th}>Carteira</th>
              <th style={styles.th}>Ambientes</th>
              <th style={styles.th}>Ultimo Login</th>
              <th style={styles.th}>Criado em</th>
              <th style={styles.th}>Acoes</th>
            </tr>
          </thead>
          <tbody>
            {loading ? Array.from({ length: 8 }).map((_, i) => (
              <tr key={i}>
                {Array.from({ length: 11 }).map((_, j) => (
                  <td key={j} style={styles.td}><Skeleton width={j === 0 ? 160 : 60} /></td>
                ))}
              </tr>
            )) : users.length === 0 ? (
              <tr><td colSpan={11} style={{ ...styles.td, textAlign: 'center', padding: 40, color: T.textMuted }}>Nenhum usuario encontrado</td></tr>
            ) : users.map(u => {
              const isOnline = u.last_activity_at && (Date.now() - new Date(u.last_activity_at).getTime()) < 5 * 60_000;
              const plan = u.subscription_plan_active || 'free';
              return (
                <tr key={u.id} style={{ cursor: 'pointer' }} onClick={() => route(`/admin/users/${u.id}`)}>
                  <td style={styles.td}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <Avatar name={u.name} url={u.avatar_url} />
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{u.name || '—'}</div>
                        <div style={{ fontSize: 11, color: T.textSecondary }}>{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td style={styles.td}><Badge text={u.role} color={u.role === 'ADMIN' ? T.warning : T.textMuted} /></td>
                  <td style={styles.td}><Badge text={u.is_suspended ? 'Suspenso' : u.status || 'ativo'} color={u.is_suspended ? T.danger : T.success} /></td>
                  <td style={styles.td}><Badge text={plan.toUpperCase()} color={PLAN_COLORS[plan] || T.textMuted} /></td>
                  <td style={styles.td}><span style={{ fontSize: 14 }}>{isOnline ? '🟢' : '⚫'}</span></td>
                  <td style={styles.td}><SparkLine data={[]} /></td>
                  <td style={styles.td}><span style={{ fontFamily: 'monospace', fontSize: 12 }}>${((u.wallet_balance ?? 0) / 100).toFixed(2)}</span></td>
                  <td style={styles.td}><span style={{ fontFamily: 'monospace' }}>{u.max_machines ?? 0}</span></td>
                  <td style={styles.td}><span style={{ fontSize: 12, color: T.textMuted }}>{timeAgo(u.last_login_at)}</span></td>
                  <td style={styles.td}><span style={{ fontSize: 12, color: T.textMuted }}>{formatDate(u.created_at)}</span></td>
                  <td style={styles.td} onClick={(e) => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <Button variant="ghost" size="xs" onClick={() => route(`/admin/users/${u.id}`)} style={{ background: T.bgSecondary, color: T.primary }}>👁</Button>
                      <Button variant={u.is_suspended ? 'success' : 'danger'} size="xs" onClick={() => handleSuspend(u.id, !!u.is_suspended)}>
                        {u.is_suspended ? '✅' : '🚫'}
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16, alignItems: 'center' }}>
          <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Anterior</Button>
          <span style={{ fontSize: 13, color: T.textSecondary }}>Pagina {page} de {totalPages} ({total} usuarios)</span>
          <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Proxima →</Button>
        </div>
      )}
    </div>
  );
}
